import React, { useState } from "react";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Grid,
  Box,
  Typography,
  Container,
  Modal,
  IconButton,
  SvgIcon,
  Snackbar,
  Alert,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import CloseIcon from "@mui/icons-material/Close";
import leafLeft from "../../Asset/icon/leafLeft.svg";
import leafRight from "../../Asset/icon/leafRight.svg";
import { useDispatch } from "react-redux";
import { RegisterNew } from "../../redux/Auth/AuthSlice";
import { useTranslation } from "react-i18next";
import "../../Asset/scss/takemyyacht.scss";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

const SignUpModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    allowExtraEmails: 0,
  });

  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === "checkbox" ? (checked ? 1 : 0) : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: updatedValue,
    }));

    setFormErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (typeof updatedValue === "string" && updatedValue.trim()) {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const validateForm = () => {
    let errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName) errors.firstName = t("First name is required");
    if (!formData.lastName) errors.lastName = t("Last name is required");
    if (!formData.email) {
      errors.email = t("Email is required");
    } else if (!emailRegex.test(formData.email)) {
      errors.email = t("Please enter a valid email address");
    }
    if (!formData.password) {
      errors.password = t("Password is required");
    } else if (
      formData.password.length < 8 ||
      !/[!@#$%^&*(),.?":{}|<>0-9a-zA-Z]/.test(formData.password)
    ) {
      errors.password = t(
        "Password must be at least 8 characters long and contain a mix of letters, numbers, and special characters"
      );
    }
    if (!formData.confirmPassword)
      errors.confirmPassword = t("Confirm password is required");
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t("Passwords do not match");
    }
    return errors;
  };

  const handleCustomGoogleRegister = () => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id:
        "668660044518-0mh6uijr15pbrv3s1arirs5al9hqkp1u.apps.googleusercontent.com",
      scope: "openid email profile",
      callback: async (tokenResponse) => {
        if (tokenResponse.access_token) {
          try {
            // ดึงข้อมูลโปรไฟล์จาก Google
            const res = await fetch(
              "https://www.googleapis.com/oauth2/v3/userinfo",
              {
                headers: {
                  Authorization: `Bearer ${tokenResponse.access_token}`,
                },
              }
            );
            const profile = await res.json();
            const { email, given_name, family_name, picture } = profile;

            // เรียก RegisterNew (สมัครสมาชิก)
            const dataRegister = await dispatch(
              RegisterNew({
                firstname: given_name,
                lastname: family_name,
                email,
                password: "",
                user_image: picture || "",
                receiveEmail: 1,
                provider: "google",
              })
            );

            if (dataRegister.status) {
              setFormData({
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                confirmPassword: "",
                allowExtraEmails: 0,
              });
              setError("");
              onClose();
            } else {
              setError(t(dataRegister.msg));
            }
          } catch (err) {
            console.error(err);
            setError(t("Registration failed. Please try again."));
          }
        } else {
          setError(t("Google login was unsuccessful"));
        }
      },
    });

    client.requestAccessToken({ prompt: "select_account consent" });
    // เปิด popup ทันที
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      try {
        const dataRegister = await dispatch(
          RegisterNew({
            firstname: formData.firstName,
            lastname: formData.lastName,
            email: formData.email,
            password: formData.password,
            receiveEmail: formData.allowExtraEmails,
            provider: "email",
          })
        );

        if (dataRegister.status) {
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            confirmPassword: "",
            allowExtraEmails: 0,
          });
          setError("");
          onClose();
        } else {
          // หากพบว่าอีเมลมีบัญชีอยู่แล้ว
          setError(t(dataRegister.msg)); // ใช้ข้อความจากเซิร์ฟเวอร์ที่ส่งกลับมา
        }
      } catch (error) {
        setError("Registration failed. Please try again.");
        console.error("Registration error:", error);
      }
    }
  };

  const handleFacebookRegister = async (response) => {
    if (!response || response.status === "unknown") {
      setError(t("Facebook login failed. Please try again."));
      return;
    }

    const { first_name, last_name, email, picture } = response;

    try {
      const dataRegister = await dispatch(
        RegisterNew({
          firstname: first_name,
          lastname: last_name,
          email: email,
          password: "",
          user_image: picture?.data?.url || "",
          receiveEmail: 1,
          provider: "facebook",
        })
      );

      if (dataRegister.status) {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: "",
          allowExtraEmails: 0,
        });
        setError("");
        onClose();
      } else {
        // หากพบว่าอีเมลมีบัญชีอยู่แล้ว
        setError(t(dataRegister.msg)); // ใช้ข้อความจากเซิร์ฟเวอร์
      }
    } catch (error) {
      setError(t("Registration failed. Please try again."));
      console.error("Facebook Registration error:", error);
    }
  };

  // const handleGoogleRegister = async (credentialResponse) => {
  //   if (!credentialResponse || !credentialResponse.credential) {
  //     setError(t("Google login failed. Please try again."));
  //     return;
  //   }

  //   const decoded = jwtDecode(credentialResponse.credential);

  //   try {
  //     const dataRegister = await dispatch(
  //       RegisterNew({
  //         firstname: decoded.given_name || "",
  //         lastname: decoded.family_name || "",
  //         email: decoded.email,
  //         password: "",
  //         user_image: decoded.picture || "",
  //         receiveEmail: 1,
  //         provider: "google",
  //       })
  //     );

  //     if (dataRegister.status) {
  //       setFormData({
  //         firstName: "",
  //         lastName: "",
  //         email: "",
  //         password: "",
  //         confirmPassword: "",
  //         allowExtraEmails: 0,
  //       });
  //       setError("");
  //       onClose();
  //     } else {
  //       // หากพบว่าอีเมลมีบัญชีอยู่แล้ว
  //       setError(t(dataRegister.msg)); // ใช้ข้อความจากเซิร์ฟเวอร์
  //     }
  //   } catch (error) {
  //     setError(t("Registration failed. Please try again."));
  //     console.error("Google Registration error:", error);
  //   }
  // };

  return (
    <Modal open={open} onClose={onClose}>
      <Container className="modalContent">
        <Box className="boxContainer">
          <IconButton className="iconButtonClose" onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <div className="d-flex align-items-center centerAlign">
            <img
              className="img-fluid"
              draggable={false}
              src={leafLeft}
              alt="leftImage"
            />
            <h3 className="textColorPrimary m-2">{t("Sign Up")}</h3>
            <img
              className="img-fluid"
              draggable={false}
              src={leafRight}
              alt="rightImage"
            />
          </div>
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 3 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  className="textField"
                  size="medium"
                  required
                  fullWidth
                  name="firstName"
                  label={t("First Name")}
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className="textField"
                  size="medium"
                  required
                  fullWidth
                  name="lastName"
                  label={t("Last Name")}
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className="textField"
                  size="medium"
                  required
                  fullWidth
                  name="email"
                  label={t("Email Address")}
                  value={formData.email}
                  onChange={handleChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className="textField"
                  size="medium"
                  required
                  fullWidth
                  name="password"
                  label={t("Password")}
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className="textField"
                  size="medium"
                  required
                  fullWidth
                  name="confirmPassword"
                  label={t("Confirm Password")}
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="allowExtraEmails"
                      checked={formData.allowExtraEmails === 1}
                      onChange={handleChange}
                    />
                  }
                  label={t("ReceiveEmailPrompt")}
                  className="checkboxLabel"
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="buttonSubmit"
            >
              {t("Sign Up")}
            </Button>
            <Box className="boxDivider">
              <Box className="dividerLine" />
              <Typography variant="body2" className="orText">
                {t("OR")}
              </Typography>
              <Box className="dividerLine" />
            </Box>

            <FacebookLogin
              appId="1723338101781841"
              autoLoad={false}
              fields="first_name,last_name,email,picture"
              callback={handleFacebookRegister}
              render={(renderProps) => (
                <Button
                  fullWidth
                  variant="outlined"
                  className="buttonOutlinedfb"
                  onClick={renderProps.onClick}
                  startIcon={
                    <SvgIcon className="fbIcon">
                      <path d="M22.675 0h-21.35C0.602 0 0 0.604 0 1.352v21.295C0 23.396 0.604 24 1.352 24H12.82v-9.294H9.692V10.51h3.127V7.797c0-3.1 1.892-4.792 4.656-4.792 1.325 0 2.465.099 2.795.143v3.24l-1.917.001c-1.506 0-1.796.717-1.796 1.767v2.314h3.593l-.468 3.195h-3.126V24h6.128C23.397 24 24 23.396 24 22.648V1.352C24 0.604 23.396 0 22.675 0z" />
                    </SvgIcon>
                  }
                >
                  {t("Sign Up with Facebook")}
                </Button>
              )}
            />

            <Button
              fullWidth
              variant="outlined"
              className="buttonOutlinedGoogle"
              startIcon={<GoogleIcon className="googleIcon" />}
              onClick={handleCustomGoogleRegister}
            >
              {t("Sign Up with Google")}
            </Button>

            <Snackbar
              open={!!error}
              autoHideDuration={6000}
              onClose={() => setError("")}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{
                width: "auto",
              }}
            >
              <Alert
                sx={{
                  borderRadius: 8,
                }}
                elevation={6}
                variant="filled"
                onClose={() => setError("")}
                severity="error"
              >
                {error}
              </Alert>
            </Snackbar>

            <Grid
              container
              justifyContent="center"
              className="mt-8"
            >
              <Grid item>
                <Typography variant="body2" className="alreadyHaveAccount">
                  {t("AlreadyHaveAccount")}
                  <Link href="#" onClick={onClose} className="loginLink">
                    {t("Login")}
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </Modal>
  );
};

export default SignUpModal;
